import type { RiskIndicator } from '@/domains/shared';
import type { RiskIndicatorForUI, RiskLevelData } from '@/shared/models';

/**
 * UseCase para transformar indicadores de riesgo a formato UI con porcentajes visuales
 *
 * Responsabilidades:
 * - Recibir indicadores de riesgo de dominio (bajo/medio/alto como números)
 * - Calcular porcentajes de cada nivel de riesgo
 * - Asignar colores para visualización (verde/amarillo/rojo)
 * - Transformar a estructura lista para componentes de UI
 */
export class ExtractRiskIndicatorsOfCareerLayerUseCase {
  /**
   * Calcula porcentajes de cada nivel de riesgo con su color asignado
   * @param indicator - Indicador de dominio con valores absolutos
   */
  private calculateRiskLevelPercentages(
    indicator: RiskIndicator,
  ): RiskLevelData[] {
    const total = indicator.bajo + indicator.medio + indicator.alto;

    if (total === 0) {
      return [
        { percentage: 0, color: 'border-green-400' },
        { percentage: 0, color: 'border-yellow-400' },
        { percentage: 0, color: 'border-red-400' },
      ];
    }

    return [
      {
        percentage: Math.round((indicator.bajo / total) * 100),
        color: 'border-green-400',
      },
      {
        percentage: Math.round((indicator.medio / total) * 100),
        color: 'border-yellow-400',
      },
      {
        percentage: Math.round((indicator.alto / total) * 100),
        color: 'border-red-400',
      },
    ];
  }

  /**
   * Transforma array de indicadores de dominio a formato UI
   * @param indicators - Indicadores con valores absolutos
   */
  private transformToUI(indicators: RiskIndicator[]): RiskIndicatorForUI[] {
    return indicators.map((indicator) => ({
      nombre: indicator.nombre,
      riskLevelData: this.calculateRiskLevelPercentages(indicator),
    }));
  }

  /**
   * Ejecuta la transformación de indicadores a formato UI
   * @param indicators - Array de indicadores de dominio
   */
  execute(indicators: RiskIndicator[]): RiskIndicatorForUI[] {
    if (!indicators || indicators.length === 0) {
      return [];
    }

    return this.transformToUI(indicators);
  }
}
